import {
  Button,
  createStyles,
  Grid,
  makeStyles,
  Theme
} from "@material-ui/core";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import React from "react";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import Header from "../common/Header";
import { APIState } from "../utils/api/reducer";
import { useQueryParams } from "../utils/queryParams";
import Heading from "./Heading";
import PlayerInputs from "./playerInputs";
import ScoringInputs from "./scoringInput";
import { setAuth, setFfOptions, setPlayers } from "./store/actions";
import { SelectedPlayers } from "./store/types";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
      minHeight: "100vh",
      paddingTop: 24,
      backgroundColor: "#102341",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      [theme.breakpoints.down("sm")]: {
        paddingBottom: 0,
      },
    },
    cornerBackground: {
      height: "100%",
      width: "100%",
      position: "absolute",
      top: 0,
      left: 0,
      zIndex: 100,
      background: "linear-gradient(#015ACE, #102341)",
      clipPath: "polygon(0 0, 453px 0, 0% 430px)",
    },
    item: {
      margin: "24px 0",
      [theme.breakpoints.down("sm")]: {
        margin: "12px 0 6px 0",
      },
      zIndex: 100,
    },
    buttonWrapper: {
      zIndex: 0,
    },
    scoringWrapper: {
      zIndex: 10,
    },
    button: {
      backgroundColor: "#015ACE50",
      padding: 16,
      fontSize: 24,
      fontWeight: 700,
      margin: "12px 0",
      color: theme.palette.common.white,
      borderRadius: 12,
      boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
      "&:hover": {
        backgroundColor: "#015ACE50",
      },
    },
  })
);

type PlayerCompareParams = {
  readonly league: string;
};

interface ReduxProps {
  readonly selectedPlayers: SelectedPlayers;
  readonly selectedScoring: string;
  readonly authenticated: APIState<any>;
}

interface DispatchProps {
  readonly SetPlayers: (
    leagueCode: string,
    site: string,
    token: string
  ) => void;
  readonly SetFfOptions: (leagueCode: string) => void;
  readonly SetAuth: (leagueCode: string, token: string) => void;
}

type Props = ReduxProps & DispatchProps;

const StartSit = (props: Props) => {
  const {
    selectedPlayers,
    selectedScoring,
    SetPlayers,
    SetFfOptions,
    SetAuth,
  } = props;
  let { league } = useParams<PlayerCompareParams>();
  let queryParams = useQueryParams();
  const token = queryParams.get("token") || "";

  const classes = useStyles();

  const getHref = () => {
    let side1Query = "";
    let side2Query = "";

    selectedPlayers.side1.forEach((s: string, i) => {
      if (i > 0) {
        side1Query += `+${s}`;
      } else {
        side1Query += s;
      }
    });
    selectedPlayers.side2.forEach((s: string, i) => {
      if (i > 0) {
        side2Query += `+${s}`;
      } else {
        side2Query += s;
      }
    });

    return `/startsit/${league}/compare?site=${selectedScoring}&token=${token}&side1=${encodeURIComponent(
      side1Query
    )}&side2=${encodeURIComponent(side2Query)}`;
  };

  React.useEffect(() => {
    SetPlayers(league, selectedScoring, token);
    SetFfOptions(league);
    SetAuth(league, token);
  }, [league, SetPlayers, token, SetAuth]);

  return (
    <div className={classes.root}>
      <Header />
      <div className={classes.cornerBackground}></div>
      <Grid container>
        <Grid item xs={12} className={classes.item}>
          <Heading />
        </Grid>
        <Grid item xs={12} className={classes.item}>
          <PlayerInputs />
        </Grid>
        <Grid
          item
          xs={12}
          className={`${classes.item} ${classes.scoringWrapper}`}
        >
          <ScoringInputs />
        </Grid>
        <Grid
          container
          item
          xs={12}
          justifyContent="center"
          className={`${classes.item} ${classes.buttonWrapper}`}
        >
          <Button
            startIcon={<FlashOnIcon />}
            href={getHref()}
            className={classes.button}
            size="large"
            disabled={
              selectedPlayers.side1.length !== selectedPlayers.side2.length ||
              selectedPlayers.side1.length < 1 ||
              selectedPlayers.side2.length < 1
            }
          >
            Compare Players
          </Button>
        </Grid>
      </Grid>
    </div>
  );
};

const mapStateToProps = (state: any) => {
  const { selectedPlayers, selectedScoring, authenticated } = state.startSit;
  return {
    selectedPlayers,
    selectedScoring,
    authenticated,
  };
};

const mapDispatchToProps = {
  SetPlayers: setPlayers,
  SetFfOptions: setFfOptions,
  SetAuth: setAuth,
};

export default connect(mapStateToProps, mapDispatchToProps)(StartSit);
